import { ExternalApiEmmiters } from './ExternalApiEmmiters';
import { observable, makeObservable, action } from 'mobx';
import { assertNever } from 'src_common/common/assertNever';
import { SdkCustomer } from 'src/domains/layouts/state/customer';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { LoginResponseType } from 'src_common/sdk/session';
import { getErrorByCode } from 'src/domains/layouts/webview/components/common/errorMessage/errors';
import { TrpcClient } from 'src/appState/TrpcClient';
import { Common } from 'src/domains/common/Common';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { CasinoAppVersionModelType } from 'src/api/config/cms_new/mobileCasinoVersion/getMobileCasinoVersion';
import { VerifyAccountState } from 'src/domains/players/state/VerifyAccountState';
import { loginUserWithGeo } from 'src/domains/players/state/accountState/AccountState';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import queryString from 'query-string';
import { parseMarketAndSelectionNewFormat } from 'src/universes/star/AppState/starAppStateInitAddToBetslip';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import { mapErrorLabel } from 'src/domains/players/state/starLoginState/StarLoginState.helper';

const isAppsFlyerIntegration: boolean = false;

type UnsubscribeType = () => void;
type SubscribeCallbackType = () => void;
type ParametersCallbackType = (res: string) => void;

interface ExternalApiInterfaceType {
    isUserLogin(): void;
    setIsLoginDisabled(isLoginDisabled: boolean): void;
    logoutDontMoveToLoginPage(isLogoutDontMoveToLoginPage: boolean): void;
    onLoginRequired(callback: SubscribeCallbackType): UnsubscribeType;
    onLogoutRequired(callback: SubscribeCallbackType): UnsubscribeType;
    openSignUp(): void;
    loginUser(email: string, password: string, callback: ParametersCallbackType): void;
    loginUserWithInfo(
        email: string,
        password: string,
        callback: (res: LoginUserWithInfoResponseType) => void
    ): Promise<void>;
    getAuthorizationInfo(callback: (res: CheckIfAuthorizedType) => void): void;
    getConnectionInfo(callback: (res: CheckIfConnectedType) => void): void;
    getMobileAppUpdateList(callback: (res: GetMobileAppListoResponseType) => void): void;
    onCameraRequired(callback: () => void | null): void;
    setCallbackOpenAppSettings(callback: () => void | null): void;
    requestPasswordReset(email: string, callback: (status: ResetPasswordType) => void): void;
    getLoginErrorTranslation(error: string): string;
}

type CheckIfAuthorizedType =
    | {
          responseType: 'ok';
          isAuthorized: boolean;
          userId: number | null;
      }
    | {
          responseType: 'error';
          message: string;
      };
type CheckIfConnectedType =
    | {
          responseType: 'ok';
      }
    | {
          responseType: 'error';
      };

type ResetPasswordType = {
    responseType: 'ok' | 'error';
    message: string;
};

type LoginUserWithInfoResponseType =
    | {
          responseType: 'ok';
          userInfo: {
              email: string;
              surname: string;
              firstName: string;
              title: string | null;
              id: number;
              pushNotificationsEnabled: boolean;
          };
      }
    | {
          responseType: 'error';
          message: string;
          reset_password_require: boolean;
          email: string;
      };

type GetMobileAppListoResponseType =
    | {
          responseType: 'ok';
          value: Array<MobileAppVersionType> | null;
      }
    | {
          responseType: 'error';
          value: null;
      }
    | {
          responseType: 'loading';
          value: null;
      };

type GetAppReviewListoResponseType =
    | {
          responseType: 'ok';
          value: Array<CasinoAppVersionModelType> | null;
      }
    | {
          responseType: 'error';
          value: null;
      }
    | {
          responseType: 'loading';
          value: null;
      };

type GetAppTranslationsResponseType =
    | {
          responseType: 'ok';
          translationsValue: Record<string, string> | null;
      }
    | {
          responseType: 'error';
          translationsValue: null;
      };

const loginUserWithInfoInner = async (
    sdkCustomer: SdkCustomer,
    email: string,
    password: string,
    loginUserCallback: (email: string, password: string, type: 'login' | 'registration') => Promise<LoginResponseType>,
    getLoginErrorTranslation: (error: string, failedLoginLimit?: number | null) => string
): Promise<LoginUserWithInfoResponseType> => {
    const loginResponse = await loginUserCallback(email, password, 'login');

    switch (loginResponse.type) {
        case 'CreateSessionResponseOk': {
            const basicData = await sdkCustomer.basicData.fetch();

            return {
                responseType: 'ok',
                userInfo: {
                    email: basicData?.email ?? '',
                    surname: basicData?.surname ?? '',
                    firstName: basicData?.firstName ?? '',
                    title: basicData?.title ?? null,
                    id: loginResponse.accountId,
                    pushNotificationsEnabled: basicData?.pushNotificationsEnabled ?? false,
                },
            };
        }
        case 'CreateSessionResponseErrorAccess': {
            return {
                responseType: 'error',
                message: loginResponse.message,
                reset_password_require: loginResponse.code === 'reset_password_require',
                email: email,
            };
        }
        case 'CreateSessionResponseErrors': {
            return {
                responseType: 'error',
                message: getLoginErrorTranslation(loginResponse.error_description, loginResponse.failedLoginLimit),
                reset_password_require: false,
                email: email,
            };
        }
        default: {
            return assertNever('loginUserWithInfoInner', loginResponse);
        }
    }
};

interface MobileAppVersionType {
    app_version: string;
    force_update: boolean;
    platform: string;
    universe: string;
}

type OnyxHeadersType = {
    'X-App-Id': 'ios-app' | 'android-app';
};
export class ExternalApi implements ExternalApiInterfaceType {
    public readonly allVersionsResource: Resource<Array<MobileAppVersionType> | null>;

    @observable public isMobileAppLogout: boolean = false;
    @observable public adjustAdID: string | null = null;
    public openUserSettingsCallback: (() => void) | null = null;
    public onCameraRequiredCallback: (() => void) | null = null;

    private readonly emitersPrivate: ExternalApiEmmiters;
    private readonly router: StarRouter;

    public readonly config: ConfigComponents;
    public readonly sdkCustomer: SdkCustomer;
    public readonly env: EnvironmentState;
    public readonly trpc: TrpcClient;

    public readonly getLoginErrorTranslation: (error: string) => string;

    public readonly loginUserCallback: (email: string, password: string) => Promise<LoginResponseType>;

    public static get = AutoWeakMap.create((common: Common) => new ExternalApi(common));
    private constructor(private readonly common: Common) {
        makeObservable(this);
        this.router = StarRouter.get(common);
        this.config = ConfigComponents.get(common);
        this.sdkCustomer = SdkCustomer.get(common);
        this.env = EnvironmentState.get(common);
        this.trpc = common.trpcClient;

        this.emitersPrivate = this.router.externalApiEmmiters;

        this.allVersionsResource = new Resource(
            async (): Promise<Array<MobileAppVersionType> | null> => this.sdkCustomer.getVersionList()
        );

        this.getLoginErrorTranslation = (message: string): string => mapErrorLabel(common, message);

        this.loginUserCallback = (email: string, password: string): Promise<LoginResponseType> =>
            loginUserWithGeo(common, email, password);
    }

    public isUserLogin(): boolean {
        return this.sdkCustomer.session.isAuthorized;
    }

    public async reviewVersions(callback: (res: GetAppReviewListoResponseType) => void): Promise<void> {
        try {
            const result = await this.trpc.client.cms.getMobileCasinoVersion.query();

            if (result.status === 200) {
                callback({ responseType: 'ok', value: result.body });
            } else {
                callback({ responseType: 'error', value: null });
            }
        } catch {
            callback({ responseType: 'error', value: null });
        }
    }

    public setIsLoginDisabled = (isLoginDisabled: boolean): void => {
        this.emitersPrivate.isLoginDisabled = isLoginDisabled;
    };
    public setSignupFinish = (): void => {
        this.emitersPrivate.emitEventSignupFinished();
    };
    public openLinkInModal = (link: string): void => {
        this.emitersPrivate.emitModalOpen(link);
    };

    @action public sendFirstLoginMsg = (id: number, pushNotificationsEnabled: boolean): void => {
        this.emitersPrivate.emitEventFirstLoginFinished(id, pushNotificationsEnabled);
    };

    public onFirstLoginMsgSend(callback: () => void): () => void {
        return this.emitersPrivate.emmiterFirstLoginFinished.on(callback);
    }
    public onOpenLinkModal(callback: () => void): () => void {
        return this.emitersPrivate.emmiterFirstLoginFinished.on(callback);
    }

    public setMobileAppLogout = (val: boolean): void => {
        this.isMobileAppLogout = val;
    };

    public setMobileAppNewExternalLinksHandling = (val: boolean): void => {
        if (val) {
            this.router.setExternalLinkOpen((url: string) => {
                this.openLinkInModal(url);
            });
        } else {
            this.router.setExternalLinkOpen(null);
        }
    };

    @action public setAdjustAppId = (v: string): void => {
        this.adjustAdID = v;
    };

    public logoutDontMoveToLoginPage = (isLogoutDontMoveToLoginPage: boolean): void => {
        this.emitersPrivate.isLogoutDontMoveToLoginPage = isLogoutDontMoveToLoginPage;
    };

    public onLoginRequired(callback: () => void): () => void {
        if (this.config.config.midnightMigrationScreen || this.config.config.closureMode) {
            return () => {};
        }
        return this.emitersPrivate.emmiterLoginClick.on(callback);
    }

    public onLogoutRequired(callback: () => void): () => void {
        return this.emitersPrivate.emmiterLogoutClick.on(callback);
    }

    public onLanguageChange(callback: () => void): () => void {
        return this.emitersPrivate.emmiterLanguageSwitcherClick.on(callback);
    }

    public sendSignupSuccessMsg(callback: () => void): () => void {
        return this.emitersPrivate.emmiterSignupFinished.on(callback);
    }

    public sendOpenLinkInModal(callback: () => void): () => void {
        return this.emitersPrivate.emmiterOpenModal.on(callback);
    }

    public async postOnyxDeviceInfo(
        headers: OnyxHeadersType,
        body: string,
        callback: (status: { responseType: 'ok' | 'error'; message?: unknown }) => void
    ): Promise<void> {
        try {
            const response = await fetch('/api-web/onyx-device-info', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ headers, body }),
            });
            const result = await response.json();
            callback(result);
        } catch (error) {
            console.error('Error calling Onyx device info endpoint:', error);
            callback({ responseType: 'error', message: error });
        }
    }

    public async getTranslations(
        language: string,
        callback: (res: GetAppTranslationsResponseType) => void
    ): Promise<void> {
        try {
            const response = await this.trpc.client.translations.getTranslations.query({ language: language });

            callback({ responseType: 'ok', translationsValue: response.translations });
        } catch (error) {
            console.error(error);
            callback({ responseType: 'error', translationsValue: null });
        }
    }

    public setAdjustAdID(addID: string, platform: string, callback: (res: string) => void): void {
        const advertisementId = JSON.stringify({ advertisementId: addID, platform: platform });
        this.setAdjustAppId(advertisementId);
        callback('Adjust app ID sent.');
    }

    public getMobileAppUpdateList(callback: (res: GetMobileAppListoResponseType) => void): void {
        try {
            const versions = this.allVersionsResource.get();
            if (versions.type === 'ready') {
                callback({ responseType: 'ok', value: versions.value });
            } else if (versions.type === 'loading') {
                callback({ responseType: 'loading', value: null });
            } else {
                callback({ responseType: 'error', value: null });
            }
        } catch {
            callback({ responseType: 'error', value: null });
        }
    }

    public openSignUp(): void {
        if (!this.isUserLogin()) {
            this.router.redirectToSignUp();
        }
    }

    public loginUser(email: string, password: string, callback: (res: string) => void): void {
        this.loginUserCallback(email, password)
            .then((res) => {
                if (res.type === 'CreateSessionResponseOk') {
                    callback('');
                } else {
                    callback('incorrect login or password');
                }
            })
            .catch((error) => {
                console.error(error);
                callback('incorrect login or password');
            });
    }

    public async loginUserWithInfo(
        email: string,
        password: string,
        callback: (res: LoginUserWithInfoResponseType) => void
    ): Promise<void> {
        if (this.config.config.midnightMigrationScreen || this.config.config.closureMode) {
            callback({
                responseType: 'error',
                message: 'Login is not available',
                reset_password_require: false,
                email,
            });
            return;
        }
        try {
            const response = await loginUserWithInfoInner(
                this.sdkCustomer,
                email,
                password,
                this.loginUserCallback,
                this.getLoginErrorTranslation
            );
            callback(response);
        } catch (error) {
            const errorString = new String(error).toString();

            callback({
                responseType: 'error',
                message: `An error occurred in the login method ${errorString}`,
                reset_password_require: false,
                email,
            });
        }
    }

    public verifyAccount(email: string, callback: (res: string) => void): void {
        VerifyAccountState.get(this.common).setEmail(email);

        this.router.redirectToVerifyAccount();
        callback('Account verified successfully');
    }

    public refreshSocket(callback: (res: string) => void): void {
        this.sdkCustomer.models.refreshSocket();
        callback('Socket has been refreshed');
    }

    public getAuthorizationInfo(callback: (res: CheckIfAuthorizedType) => void): void {
        try {
            const response = this.sdkCustomer.session.isAuthorized;
            const userId = this.sdkCustomer.session.userId;

            callback({ responseType: 'ok', isAuthorized: response, userId: userId });
        } catch (error) {
            const errorString = new String(error).toString();

            callback({
                responseType: 'error',
                message: `An error occurred in the login method ${errorString}`,
            });
        }
    }

    public getConnectionInfo(callback: (res: CheckIfConnectedType) => void): void {
        try {
            callback({ responseType: 'ok' });
        } catch {
            callback({
                responseType: 'error',
            });
        }
    }

    public setCallbackOpenAppSettings(callback: () => void | null): void {
        this.openUserSettingsCallback = callback;
    }

    public onCameraRequired(callback: () => void | null): void {
        this.onCameraRequiredCallback = callback;
    }

    public requestPasswordReset = async (
        email: string,
        callback: (status: ResetPasswordType) => void
    ): Promise<void> => {
        let resp = null;
        try {
            resp = await this.sdkCustomer.requestResetPassword({
                requestBody: {
                    email,
                    sendViaSMS: false,
                    verify: false,
                },
            });

            if (resp.status === 'ok') {
                callback({
                    responseType: 'ok',
                    message: '',
                });
                return;
            }

            if (resp.reason !== null && resp.reason !== undefined) {
                switch (resp.reason) {
                    case 'suspended': {
                        callback({
                            responseType: 'error',
                            message: getErrorByCode('ERROR_ACCOUNT_SUSPENDED'),
                        });

                        return;
                    }
                    case 'blocked': {
                        callback({
                            responseType: 'error',
                            message: getErrorByCode('ERROR_ACCOUNT_BLOCKED'),
                        });

                        return;
                    }
                    case 'closed': {
                        callback({
                            responseType: 'error',
                            message: getErrorByCode('ERROR_ACCOUNT_CLOSED'),
                        });
                        return;
                    }
                    case 'self-excluded': {
                        callback({
                            responseType: 'error',
                            message: getErrorByCode('ERROR_ACCOUNT_SELF_EXCLUDED'),
                        });
                        return;
                    }
                    default: {
                        callback({
                            responseType: 'error',
                            message: getErrorByCode('ERROR_ACCOUNT_UNKNOWN_REASON'),
                        });
                        return;
                    }
                }
            }
        } catch (err) {
            console.error(err);
        }
    };

    private getParams = (data: { platform: 'ios' | 'android'; path: string }): Record<string, string> => {
        // temporary not used - waiting for integration with appsflyer

        const { platform, path } = data;
        const parsedUrl = new URL(path);

        if (platform === 'ios') {
            // IOS
            const params = queryString.parse(parsedUrl.pathname.replaceAll('/', '').replaceAll('\\', ''));
            const parsedParams: Record<string, string> = Object.fromEntries(
                Object.entries(params).map(([key, value]) => [
                    key,
                    Array.isArray(value) ? value.join(',') : value ?? '',
                ])
            );

            return parsedParams;
        }

        // ANDROID

        const search = queryString.parse(parsedUrl.search);
        const deepLinkParam = typeof search.deep_link === 'string' ? search.deep_link : '';
        const deepLink = decodeURIComponent(deepLinkParam);

        const pathParams = deepLink.split('://')[1] ?? '';
        const params: Record<string, string> = {};
        const pathSegments = pathParams.split('&');

        pathSegments.forEach((segment) => {
            const [key, value] = segment.split('=');
            if (key !== undefined && value !== undefined) {
                params[key] = value;
            }
        });

        return params;
    };

    public redirectTo = async (payload: { platform: 'ios' | 'android'; path: string }): Promise<void> => {
        if (this.env.isBrowser === false) {
            return;
        }

        const { path } = payload;

        // direct url with domain
        if (path.startsWith(this.common.websiteBaseUrl)) {
            const relativePath = path.replace(this.common.websiteBaseUrl, '');

            if (relativePath.includes('action-add-bet-to-betslip')) {
                const urlParams = queryString.parse(relativePath);
                const addToBetslip = urlParams['/?action-add-bet-to-betslip'];

                if (typeof addToBetslip === 'string') {
                    const chunks = addToBetslip.split(',');
                    for (const chunk of chunks) {
                        const ids = parseMarketAndSelectionNewFormat(chunk);

                        if (ids !== null) {
                            const { eventId, marketId, selectionId } = ids;

                            const selectionModelId = this.common.models.id
                                .getEventId(eventId)
                                .getMarketId(marketId)
                                .getSelectionId(selectionId);

                            await selectionModelId.getModelAsync();

                            const betslipState = AppSportsBookState.get(this.common).betSlipState.betslipData;
                            betslipState.onAddSimpleBet(selectionModelId);

                            return;
                        }
                    }
                }
            }
            this.router.setUrl(relativePath);
            return;
        }

        // temporary not used - waiting for integration with appsflyer

        if (isAppsFlyerIntegration) {
            const params = this.getParams(payload);

            const sportParam = params['sport'] ?? null;
            const competitionParam = params['competition'] ?? null;
            const eventParam = params['event'] ?? null;
            const marketParam = params['market'] ?? null;
            const selectionParam = params['selection'] ?? null;

            if (eventParam !== null && marketParam !== null && selectionParam !== null) {
                const eventIdNum = parseInt(eventParam, 10);
                const marketIdNum = parseInt(marketParam, 10);
                const selectionIdNum = parseInt(selectionParam, 10);

                const selectionId = this.common.models.id
                    .getEventId(eventIdNum)
                    .getMarketId(marketIdNum)
                    .getSelectionId(selectionIdNum);

                const selectionModel = await selectionId.getModelAsync();

                // TODO fix that - https://playbookengineering.atlassian.net/browse/TTX-404
                // @ts-expect-error
                window.$appState.appSportsBookState.betSlipState.legsState.betslipData.onAddSimpleBet(
                    selectionModel.id2
                );

                return;
            }

            if (eventParam !== null) {
                const eventIdNum = parseInt(eventParam, 10);

                const eventId = this.common.models.id.getEventId(eventIdNum);

                this.router.redirectToEvent(eventId);

                return;
            }

            if (sportParam !== null && competitionParam !== null) {
                const competitionIdNum = parseInt(competitionParam, 10);

                this.router.redirectToCompetitionPage(competitionIdNum, sportParam);

                return;
            }

            if (sportParam !== null) {
                this.router.redirectToSportPage(sportParam);
                return;
            }
        }
    };
}

/*
For testing

IOS
$appState.externalApi.onLoginRequired(() => {
    console.info("dsadsa");
});
$appState.externalApi.setIsLoginDisabled(true);


Android
$appState.externalApi.onLoginRequired(() => {
    console.info("dsadsa");
});
$appState.externalApi.setIsLoginDisabled(true);
$appState.externalApi.logoutDontMoveToLoginPage(true);
*/
